import { createContext, useContext, useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { translations, languages, getStoredLanguage, setStoredLanguage } from '../lib/i18n'

const I18nContext = createContext()

export const useTranslation = () => {
  const context = useContext(I18nContext)
  if (!context) {
    throw new Error('useTranslation must be used within an I18nProvider')
  }
  return context
}

export const useLanguage = () => {
  const context = useContext(I18nContext)
  if (!context) {
    throw new Error('useLanguage must be used within an I18nProvider')
  }
  return context
}

export const I18nProvider = ({ children }) => {
  const location = useLocation()
  const [currentLanguage, setCurrentLanguage] = useState(getStoredLanguage())
  const [isLoading, setIsLoading] = useState(false)

  // 检测 URL 中的语言前缀 (例如 /zh/xxx -> zh)
  useEffect(() => {
    const pathParts = location.pathname.split('/').filter(Boolean)
    const langInPath = pathParts[0]
    
    if (languages[langInPath]) {
      if (langInPath !== currentLanguage) {
        setCurrentLanguage(langInPath)
        setStoredLanguage(langInPath)
      }
    }
  }, [location.pathname, currentLanguage])

  // Get current translations
  const t = (key, params = {}) => {
    const keys = key.split('.')
    let value = translations[currentLanguage]
    
    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k]
      } else {
        // Fallback to English if translation not found
        value = translations.en
        for (const fallbackKey of keys) {
          if (value && typeof value === 'object') {
            value = value[fallbackKey]
          } else {
            return key // Return key if no translation found
          }
        }
        break
      }
    }
    
    // Replace parameters in translation
    if (typeof value === 'string') {
      Object.keys(params).forEach(param => {
        value = value.replace(`{{${param}}}`, params[param])
      })
    }
    
    return value || key
  }

  // Change language
  const changeLanguage = async (languageCode) => {
    if (!languages[languageCode]) {
      console.error(`Language ${languageCode} not supported`)
      return
    }

    setIsLoading(true)
    
    try {
      setCurrentLanguage(languageCode)
      setStoredLanguage(languageCode)
      
      // Update document language
      document.documentElement.lang = languageCode
      
      // Dispatch language change event
      window.dispatchEvent(new CustomEvent('languageChanged', {
        detail: { language: languageCode }
      }))
      
    } catch (error) {
      console.error('Error changing language:', error)
    } finally {
      setIsLoading(false)
    }
  }

  // Get available languages
  const getAvailableLanguages = () => {
    return Object.values(languages)
  }

  // Get current language info
  const getCurrentLanguageInfo = () => {
    return languages[currentLanguage]
  }

  // Initialize language on mount
  useEffect(() => {
    const storedLang = getStoredLanguage()
    if (storedLang !== currentLanguage) {
      setCurrentLanguage(storedLang)
    }
    
    // Set document language
    document.documentElement.lang = currentLanguage
    
    // Set document direction for RTL languages (if needed in future)
    const isRTL = ['ar', 'he', 'fa'].includes(currentLanguage)
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr'
  }, [currentLanguage])

  const value = {
    // Translation function
    t,
    
    // Language management
    currentLanguage,
    changeLanguage,
    getAvailableLanguages,
    getCurrentLanguageInfo,
    
    // State
    isLoading,
    
    // Direct access to translations
    translations: translations[currentLanguage]
  }

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  )
}

export default I18nProvider




